import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, X, Piano, Guitar, Drum, Mic2, Radio, MessageSquare, Clock, Sparkles, Target, Users, BadgeCheck, Infinity as InfinityIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const IMG = {
  piano: "/media/jt-grand-piano.jpg",
  guitar: "https://images.unsplash.com/photo-1614999098814-23c48ffa512d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHw0fHxhY291c3RpYyUyMGd1aXRhciUyMG11c2ljaWFuJTIwcGxheWluZyUyMGxpdmV8ZW58MHx8fHwxNzkxNzUzNDY2fDA&ixlib=rb-4.1.0&q=85",
  drums: "https://images.unsplash.com/photo-1524230659092-07f99a75c013?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwyfHxkcnVtbWVyJTIwcGxheWluZyUyMGRydW1zJTIwY29uY2VydCUyMGxpZ2h0aW5nfGVufDB8fHx8MTc5MTc1MzQ2Nnww&ixlib=rb-4.1.0&q=85",
  voice: "https://images.unsplash.com/photo-1689771455000-7ca175aa03fe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwyfHxzaW5nZXIlMjB2b2NhbCUyMG1pY3JvcGhvbmUlMjBzdHVkaW8lMjBtdXNpY3xlbnwwfHx8fDE3OTE3NTM0NjZ8MA&ixlib=rb-4.1.0&q=85",
};

const INSTRUMENTS = [
  { id: "piano", label: "Piano", icon: Piano, to: "/instruments/pianists", blurb: "Read, play and accompany — the full keyboard journey." },
  { id: "guitar", label: "Guitar", icon: Guitar, to: "/instruments/guitarists", blurb: "From open chords to reading on the fretboard." },
  { id: "drums", label: "Drums", icon: Drum, to: "/instruments/drummers", blurb: "Rhythmic notation, grooves and sight-reading." },
  { id: "voice", label: "Voice", icon: Mic2, to: "/instruments/singers", blurb: "Pitch, intervals and reading for singers." },
];

export const CourseCards = ({ heading = true }) => (
  <section id="courses" className="mx-auto max-w-7xl px-4 sm:px-8 py-16 lg:py-20" data-testid="courses-section">
    {heading && (
      <div className="max-w-2xl">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">Choose your instrument</p>
        <h2 className="mt-3 font-serif text-3xl font-extrabold tracking-tight sm:text-4xl">Four instruments. One clear path.</h2>
        <p className="mt-3 text-muted-foreground">Beginner to confident reader in just 10 focused lessons.</p>
      </div>
    )}
    <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
      {INSTRUMENTS.map((c, i) => (
        <motion.div key={c.id} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}>
          <Link to={c.to} data-testid={`course-card-${c.id}`} className="group block overflow-hidden rounded-2xl border border-border bg-card transition-all hover:-translate-y-1 hover:border-primary hover:shadow-[0_0_30px_-8px_hsl(var(--primary)/0.4)]">
            <div className="relative aspect-[4/3] overflow-hidden">
              <img src={IMG[c.id]} alt={c.label} className={`h-full w-full object-cover transition-transform ${c.id === "piano" ? "object-[center_60%]" : ""} duration-500 group-hover:scale-105`} />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <span className="absolute left-3 top-3 flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground"><c.icon size={18} /></span>
              {c.soon && <span className="absolute right-3 top-3 rounded-full bg-black/60 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-primary backdrop-blur">Coming soon</span>}
              <h3 className="absolute bottom-3 left-4 font-serif text-2xl font-bold text-white">{c.label}</h3>
            </div>
            <div className="p-5">
              <p className="text-sm text-muted-foreground">{c.blurb}</p>
              <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary">
                {c.soon ? "Join the waitlist" : "Start learning"} <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
              </span>
            </div>
          </Link>
        </motion.div>
      ))}
    </div>
  </section>
);

const COMPARE = [
  { label: "A structured path from zero to reading", jt: true, yt: false },
  { label: "Built around just 10 focused lessons", jt: true, yt: false },
  { label: "Real-time sight-reading feedback & quizzes", jt: true, yt: false },
  { label: "Taught by a 30-year pro & Musical Director", jt: true, yt: false },
  { label: "Progress tracking, streaks & certificates", jt: true, yt: false },
  { label: "Ask questions in live Q&A sessions", jt: true, yt: false },
  { label: "Endless random videos with no plan", jt: false, yt: true },
  { label: "No idea if you're actually improving", jt: false, yt: true },
];

export const WhyYouTube = () => (
  <section id="why-youtube" className="border-y border-border bg-card">
    <div className="mx-auto max-w-5xl px-4 sm:px-8 py-16 lg:py-24">
      <div className="max-w-2xl">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">Why not just YouTube?</p>
        <h2 className="mt-3 font-serif text-3xl font-extrabold tracking-tight sm:text-4xl">Some free videos teach tricks. I teach you to read. Full stop.</h2>
        <p className="mt-3 text-muted-foreground">One proven path — so you get there.</p>
      </div>
      <div className="mt-10 overflow-hidden rounded-2xl border border-border" data-testid="why-youtube-comparison-table">
        <div className="grid grid-cols-[1fr_auto_auto] items-center gap-2 border-b border-border bg-background px-4 py-3 text-sm font-bold sm:px-6">
          <span className="text-muted-foreground">What matters</span>
          <span className="w-20 text-center text-primary">The JT Hub</span>
          <span className="w-20 text-center text-muted-foreground">YouTube</span>
        </div>
        {COMPARE.map((r, i) => (
          <div key={i} className={`grid grid-cols-[1fr_auto_auto] items-center gap-2 px-4 py-3.5 text-sm sm:px-6 ${i % 2 ? "bg-background/50" : ""}`}>
            <span>{r.label}</span>
            <span className="flex w-20 justify-center">{r.jt ? <Check size={18} className="text-primary" /> : <X size={16} className="text-muted-foreground/40" />}</span>
            <span className="flex w-20 justify-center">{r.yt ? <Check size={16} className="text-muted-foreground/60" /> : <X size={16} className="text-muted-foreground/40" />}</span>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const REASONS = [
  { icon: Clock, title: "Just 10 lessons", desc: "A tight, proven curriculum — no filler. You read music as required by lesson ten." },
  { icon: BadgeCheck, title: "100% exam pass rate", desc: "Three decades of teaching with every private exam student passing." },
  { icon: Sparkles, title: "World-class experience", desc: "Musician and MD for many well-known artists — and performed for the Pope." },
  { icon: Target, title: "Real feedback", desc: "Interactive sight-reading & quizzes tell you exactly where you stand." },
  { icon: Users, title: "A real community", desc: "Live Q&A and a members' board — learn alongside others, with me." },
  { icon: InfinityIcon, title: "Skills for life", desc: "Reading music opens every instrument, every genre, forever." },
];

export const WhyChoose = () => (
  <section id="why-choose" className="mx-auto max-w-7xl px-4 sm:px-8 py-16 lg:py-24">
    <div className="max-w-2xl">
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">Why choose The JT Hub</p>
      <h2 className="mt-3 font-serif text-3xl font-extrabold tracking-tight sm:text-4xl">Learn from someone who's lived it.</h2>
      <p className="mt-3 text-muted-foreground">A professional musician who'll get you reading, fast.</p>
    </div>
    <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
      {REASONS.map((r, i) => (
        <motion.div key={r.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.06 }}
          className="rounded-2xl border border-border bg-card p-6" data-testid={`why-reason-${i}`}>
          <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary"><r.icon size={22} /></span>
          <h3 className="mt-4 font-serif text-xl font-semibold">{r.title}</h3>
          <p className="mt-2 text-sm text-muted-foreground">{r.desc}</p>
        </motion.div>
      ))}
    </div>
  </section>
);

function fmtWhen(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch (e) {
    return iso;
  }
}

export const LiveQATeaser = () => {
  const [next, setNext] = useState(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    api.get("/live-sessions").then(({ data }) => setNext(data.upcoming?.[0] || null)).catch(() => {}).finally(() => setLoaded(true));
  }, []);
  return (
    <section id="live-qa" className="border-y border-border bg-card">
      <div className="mx-auto flex max-w-6xl flex-col items-start gap-8 px-4 sm:px-8 py-16 lg:flex-row lg:items-center lg:py-20">
        <div className="flex-1">
          <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-primary"><Radio size={14} /> Live with Jason</p>
          <h2 className="mt-3 font-serif text-3xl font-extrabold tracking-tight sm:text-4xl">Join a live Q&A session</h2>
          <p className="mt-3 max-w-xl text-muted-foreground">Bring your questions and play along. Register and I'll send you the join link.</p>
        </div>
        <div className="w-full rounded-2xl border border-primary/30 bg-background p-6 lg:w-80">
          {!loaded ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : next ? (
            <>
              <p className="font-mono text-[10px] uppercase tracking-widest text-primary">Next session</p>
              <p className="mt-2 font-serif text-xl font-semibold leading-snug">{next.title}</p>
              <p className="mt-1 text-sm text-muted-foreground">{fmtWhen(next.scheduled_at)} · {next.platform}</p>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">A new session will be announced soon. Register to hear first.</p>
          )}
          <Link to="/live" data-testid="live-qa-rsvp-button" className="mt-4 block">
            <Button className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90">{next ? "RSVP / Remind me" : "See live sessions"} <ArrowRight size={15} className="ml-2" /></Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export const CommunityTeaser = () => {
  const [threads, setThreads] = useState([]);
  useEffect(() => {
    api.get("/community/threads").then(({ data }) => setThreads((data.threads || []).slice(0, 3))).catch(() => {});
  }, []);
  return (
    <section id="community" className="mx-auto max-w-7xl px-4 sm:px-8 py-16 lg:py-20">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-primary"><MessageSquare size={14} /> Community</p>
          <h2 className="mt-3 font-serif text-3xl font-extrabold tracking-tight sm:text-4xl">Join the conversation</h2>
          <p className="mt-3 max-w-xl text-muted-foreground">Share progress, ask for tips and keep each other going — "How many hours have you practised this week?"</p>
        </div>
        <Link to="/community" data-testid="community-cta"><Button variant="outline" className="rounded-full border-border hover:border-primary hover:text-primary">Visit the board <ArrowRight size={15} className="ml-2" /></Button></Link>
      </div>
      {threads.length > 0 && (
        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          {threads.map((t) => (
            <Link key={t.id} to="/community" className="rounded-2xl border border-border bg-card p-5 transition-colors hover:border-primary">
              <p className="font-serif text-lg font-semibold leading-snug">{t.title}</p>
              <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{t.body}</p>
              <p className="mt-3 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">{t.author_name} · {t.reply_count} replies</p>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
};
